import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Cart = () => {
  const { cart, removeFromCart, getCartTotal } = useCart();
  const [cartDetails, setCartDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCartDetails();
  }, [cart]);

  const loadCartDetails = async () => {
    try {
      setLoading(true);
      const details = await Promise.all(
        cart.map(async (item) => {
          try {
            const endpoint = item.item_type === 'track' ? 'tracks' : 'albums';
            const response = await axios.get(`${API}/${endpoint}/${item.item_id}`);
            return { ...item, details: response.data };
          } catch (error) {
            console.error(`Error loading ${item.item_type}:`, error);
            return { ...item, details: null };
          }
        })
      );
      setCartDetails(details);
    } catch (error) {
      console.error('Error loading cart details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (itemId) => {
    await removeFromCart(itemId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="cart-loading">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="empty-cart">
        <ShoppingBag className="w-20 h-20 text-muted-foreground mx-auto mb-6 opacity-50" />
        <h2 className="text-3xl font-bold font-display text-foreground mb-4">
          Your cart is empty
        </h2>
        <p className="text-lg text-muted-foreground mb-8">
          Browse our collection and add some music to your cart!
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-105"
          data-testid="browse-music-btn"
        >
          Browse Music
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold font-display text-foreground mb-8" data-testid="cart-title">
          Shopping Cart
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartDetails.map((item, index) => {
              const detail = item.details;
              if (!detail) return null;

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card border border-border/50 rounded-2xl p-6 hover:border-primary/30 transition-colors"
                  data-testid={`cart-item-${item.item_id}`}
                >
                  <div className="flex gap-4">
                    {/* Cover Art */}
                    <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={detail.cover_url}
                        alt={detail.title}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Item Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <span className="inline-block px-3 py-1 bg-secondary/20 text-secondary text-xs font-bold rounded-full mb-2">
                            {item.item_type.toUpperCase()}
                          </span>
                          <h3 className="text-lg font-bold text-foreground mb-1 truncate" data-testid={`cart-item-title-${item.item_id}`}>
                            {detail.title}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate" data-testid={`cart-item-artist-${item.item_id}`}>
                            {detail.artist}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-primary font-display" data-testid={`cart-item-price-${item.item_id}`}>
                            ${item.price.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemove(item.item_id)}
                        className="mt-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
                        data-testid={`cart-remove-btn-${item.item_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border/50 rounded-2xl p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-bold font-display text-foreground mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-muted-foreground">
                  <span>Items ({cart.length})</span>
                  <span data-testid="cart-subtotal">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex justify-between text-lg font-bold text-foreground">
                    <span>Total</span>
                    <span className="text-primary font-display" data-testid="cart-total">
                      ${getCartTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-105"
                data-testid="proceed-to-checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="w-5 h-5" />
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full mt-3 px-6 py-3 text-muted-foreground hover:text-foreground transition-colors text-center"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
